import { useHistory, useParams } from "react-router-dom";
import { useEndUser } from "./hooks/useEnduser";
import EndUserSkeleton from "./components/skeletons/EndUserSkeleton";
import ErrorPage from "../shared/components/BaseError";
import EntityPageLayout from "../shared/components/EntityPageLayout";
import { useIntl } from "react-intl";
import BaseButton from "components/common/BaseButton";
import useMutateEndUser from "./hooks/useMutateEndUser";
import { useState } from "react";
import DeletionModal from "../shared/components/DeletionModal";

const ViewEndUserPage = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState("");

  const history = useHistory();

  const intl = useIntl();

  const { entityId } = useParams<{
    entityId: string;
  }>();

  const {
    data: endUser,
    isLoading,
    isError,
    error,
    refetch,
  } = useEndUser({ endUserId: entityId });

    const {
      deleteEndUser,
      deleteEndUserLoading,
      deleteEndUserSuccess,
      deleteEndUserReset,
      deleteEndUserIsError,
    } = useMutateEndUser({ id: selectedEntityId as string });
  
    const handleDelete = () => {
      const endUserName = endUser?.name || "";
      setSelectedEntityId(endUser?.id.toString() || "");
      setShowDeleteModal(true);
      setEntityName(endUserName);
    };

    const onEditClick = () => {
      history.push(`/us-admin-app/end-users/${entityId}/edit`);
    }
  
    const confirmDelete = () => {
      if (selectedEntityId) {
        deleteEndUser({ endUserId: selectedEntityId });
      }
    };

  if (isError)
    return (
      <EntityPageLayout headerMessage="usAdmin.viewEndUser">
        <ErrorPage errorMessage={error} onRetry={refetch} />
      </EntityPageLayout>
    );

  if (isLoading)
    return (
      <EntityPageLayout headerMessage="usAdmin.viewEndUser">
        <EndUserSkeleton />
      </EntityPageLayout>
    );

  return (
    <EntityPageLayout headerMessage="usAdmin.viewEndUser">
      <div className="view-grid">
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.name" })}</span>
          <span>{endUser?.name || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.contactName.endUser" })}</span>
          <span>{endUser?.contactName || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.address" })}</span>
          <span>{endUser?.address || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.city" })}</span>
          <span>{endUser?.city || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.state" })}</span>
          <span>{endUser?.state || "N/A"}</span>
        </div>
         <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "country" })}</span>
          <span>{endUser?.country?.name || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.zip" })}</span>
          <span>{endUser?.zip || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.phone" })}</span>
          <span>{endUser?.phone || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.industrySpecialist" })}</span>
          <span>{endUser?.industrySpecialist?.name || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.distributor" })}</span>
          <div>
          <span className="usadmin-distributor__header">
            {intl.formatMessage({ id: "usAdmin.contact" })}:{" "}
          </span>
          <span>{endUser?.distributor?.contactName || "N/A"} </span>
        </div>
        <div>
          <span className="usadmin-distributor__header">
            {intl.formatMessage({ id: "usAdmin.at" })}:{" "}
          </span>
          <span>{endUser?.distributor?.name || "N/A"}</span>
        </div>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.automatedRg" })}</span>
          <span>{endUser?.automatedRg || "N/A"}</span>
        </div>
      </div>
      <div className="view-action-button-container">
        <BaseButton text={intl.formatMessage({ id: "usAdmin.edit" })} handleSubmit={onEditClick} />
        <BaseButton text={intl.formatMessage({ id: "usAdmin.delete" })} backgroundColor="red" handleSubmit={handleDelete} />
      </div>
      <DeletionModal
        isOpen={showDeleteModal || deleteEndUserSuccess || deleteEndUserIsError}
        isError={deleteEndUserIsError}
        isSuccess={deleteEndUserSuccess}
        loading={deleteEndUserLoading}
        deletedEntityName={entityName}
        onClose={() => {
          setShowDeleteModal(false);
          deleteEndUserReset();
        }}
        onConfirm={confirmDelete}
      />
    </EntityPageLayout>
  );
};

export default ViewEndUserPage;
